<script setup lang="ts">
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import dayjs from 'dayjs';
import LZString from 'lz-string';

import type { TranslationForm, TranslationFormItem } from '@/types/translation';

const timezone = dayjs.tz.guess();
const defaultArrivalDate = dayjs()
	.tz(timezone)
	.add(7, 'day')
	.startOf('day')
	.utc(true)
	.format(DATE_TIME_ISO8601_FORMAT);
const defaultDepartureDate = dayjs()
	.tz(timezone)
	.add(14, 'day')
	.startOf('day')
	.utc(true)
	.format(DATE_TIME_ISO8601_FORMAT);

const formData = reactive<TranslationForm>({
	targets: [
		{
			arrive: defaultArrivalDate,
			depart: defaultDepartureDate,
			location: 'Tokyo, Japan',
			experiences: {
				tradition: false,
				food: false,
				nature: false,
				anime: false,
				workshop: false,
			},
			coordinates: null,
			countryCode: 'jp',
		},
	],
});

const router = useRouter();
const { initGeocoder, fetchLocation } = useGoogleMaps();
const notificationStore = useNotificationStore();

const isGeocoderInitialized = ref(false);
const geocoderRetryCount = ref(0);
const isSubmit = ref(false);
const isGeocoding = ref(false);

const isLoading = computed(() => false);

const rules = {
	targets: {
		$each: helpers.forEach({
			arrive: {
				required: helpers.withMessage('Please enter your travel dates.', (value: string) => {
					if (!isSubmit.value) {
						return true;
					}
					return !!value;
				}),
			},
			depart: {
				required: helpers.withMessage('Please enter your travel dates.', (value: string) => {
					if (!isSubmit.value) {
						return true;
					}
					return !!value;
				}),
				isAfterStartDate: helpers.withMessage(
					'Departure date must be on or after the arrival date.',
					(value: string, model: any) => {
						if (!isSubmit.value || !value) {
							return true;
						}
						return dayjs(value).isSameOrAfter(model.arrive);
					},
				),
			},
			location: {
				required: helpers.withMessage('Please enter your destination.', (value: string) => {
					if (!isSubmit.value) {
						return true;
					}
					return !!value;
				}),
			},
		}),
	},
};

const v$ = useVuelidate(rules, formData);

function handleDestinationChange(newData: TranslationFormItem, changeIndex: number) {
	if (!formData.targets?.length) {
		return;
	}

	formData.targets = formData.targets.map((destinationItem, index) => {
		if (index === changeIndex) {
			return {
				...destinationItem,
				...newData,
			};
		}
		return destinationItem;
	});
}

async function initSearch() {
	isGeocoding.value = true;
	const destination = formData.targets?.[0];
	if (!destination) {
		isGeocoding.value = false;
		return;
	}

	try {
		if (!isGeocoderInitialized.value) {
			throw new Error('Geocoder not initialized');
		}

		const data = {
			index: 0,
			arrive: destination.arrive,
			depart: destination.depart,
			location: destination.location,
			tags: '',
			coordinates: `${destination.coordinates?.lat},${destination.coordinates?.lng}`,
			countryCode: 'jp', // Allow only japan
		};

		if (destination.location && !destination.coordinates) {
			const { coordinates } = await fetchLocation(destination.location);
			data.coordinates = coordinates?.toUrlValue() || '';
		}

		const compressedCriteria = LZString.compressToEncodedURIComponent(JSON.stringify([data]));
		router.push({
			name: ROUTE.VISIT_JAPAN_SEARCH_RESULTS.NAME,
			query: {
				[DISCOVERY_JAPAN_QUERY_NAME.SEARCH]: compressedCriteria,
			},
		});
	} catch (error) {
		const fallback = {
			index: 0,
			arrive: destination.arrive,
			depart: destination.depart,
			location: '', // Remove location parameter as requested
			tags: '',
			coordinates: '',
			countryCode: 'jp', // Allow only japan
		};

		const compressedCriteria = LZString.compressToEncodedURIComponent(JSON.stringify([fallback]));
		router.push({
			name: ROUTE.VISIT_JAPAN_SEARCH_RESULTS.NAME,
			query: {
				[DISCOVERY_JAPAN_QUERY_NAME.SEARCH]: compressedCriteria,
			},
		});
	} finally {
		isGeocoding.value = false;
	}
}

function handleSubmit() {
	isSubmit.value = true;
	v$.value.$touch();
	if (v$.value.$error) {
		return;
	}
	initSearch();
	isSubmit.value = false;
}

async function initializeGeocoderWithRetry() {
	try {
		if (window.google?.maps) {
			await initGeocoder();
			isGeocoderInitialized.value = true;
		} else if (geocoderRetryCount.value < 3) {
			geocoderRetryCount.value++;
			setTimeout(initializeGeocoderWithRetry, 1000);
		} else {
			notificationStore.showErrorNotification(COMMON_ERROR_MESSAGE.REFRESH_PAGE);
		}
	} catch (error) {
		// Silently ignore Google Maps timeout errors (benign third-party issue)
		if (error === 'Timeout' || (error instanceof Error && error.message === 'Timeout')) {
			return;
		}

		if (geocoderRetryCount.value < 3) {
			geocoderRetryCount.value++;
			setTimeout(initializeGeocoderWithRetry, 1000);
		} else {
			notificationStore.showErrorNotification(COMMON_ERROR_MESSAGE.REFRESH_PAGE);
		}
	}
}

onMounted(() => initializeGeocoderWithRetry());
</script>

<template>
	<div class="event-form-page">
		<div class="form-header">
			<h2 class="form-title">
				DISCOVER AUTHENTIC EVENTS!
			</h2>
			<p class="form-subtitle">
				Food, anime, tradition and more
			</p>
		</div>

		<form class="form-container">
			<div
				v-if="formData.targets?.length"
				class="destinations-list"
			>
				<JapanDiscoveryInputItem
					:destination-index="0"
					:data="formData.targets[0]"
					:disabled="isLoading"
					:errors="v$.targets.$each.$response.$errors"
					@on-change="
						(emittedDestination: TranslationFormItem) =>
							handleDestinationChange(emittedDestination, 0)
					"
					@on-press-enter="handleSubmit"
				/>
			</div>
		</form>

		<div class="button-container">
			<BaseButton
				class="submit-btn"
				:loading="isGeocoding"
				:disabled="isGeocoding"
				@click="handleSubmit"
			>
				<template v-if="isGeocoding">
					<BaseProgressCircular
						:size="24"
						color="#444CE7"
					/>
				</template>
				<template v-else>
					Find events
				</template>
			</BaseButton>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.event-form-page {
	display: flex;
	flex-direction: column;
	gap: spacings-get(5);
	width: 100%;
	@include fonts-get(regular, text-sm);

	.form-header {
		text-align: center;
		margin-bottom: spacings-get(2);

		.form-title {
			@include fonts-get(bold, display-xs);
			color: colors-get(base, white);
			margin: 0 0 spacings-get(1);
			font-family: 'HF Portanic', 'Inter', Arial, sans-serif;
			font-style: italic;
			letter-spacing: rem(0.48);
		}

		.form-subtitle {
			@include fonts-get(regular, text-md);
			color: colors-get(base, white);
			font-style: italic;
			margin: 0;
			opacity: 0.9;
		}
	}

	.form-container {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		width: 100%;
		gap: spacings-get(3);

		.destinations-list {
			width: 100%;
		}
	}

	.button-container {
		display: flex;
		justify-content: center;
		margin-top: spacings-get(2);

		.submit-btn {
			width: 100%;
			background: rgb(255, 192, 192);
			color: #5d0c10;
			border: none;
			@include fonts-get(semibold, text-md);
			border-radius: rem(26);

			&:hover {
				background: rgb(255, 192, 192, 0.8);
			}
		}
	}
}
</style>
